var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { fsPhoneNumberFieldDefaults, phoneNumberFieldEventType, PhoneNumberFieldManager } from '@cof/fs-phone-number-field-core';
import { FsTextField } from '@cof/fs-text-field-wc';
import { html, nothing } from 'lit';
import { property, query } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
/**
 * ## Installation
 * ### Install the node module:
 * ```bash
 * npm i @cof/fs-phone-number-field-wc
 * ```
 *
 * ## Usage
 * To import the side-effectful registration of `fs-phone-number-field` call
 *
 * ```javascript
 * import '@cof/fs-phone-number-field-wc/fs-phone-number-field.js';
 * ```
 *
 * To import the `FsPhoneNumberField` class as either a base class or to self-register the custom element, use
 *
 * ```javascript
 * import { PhoneNumberField } from '@cof/fs-phone-number-field-wc';
 * ```
 *
 * ## Anatomy
 * ### DOM structure
 * ```html
 * <form>
 *   <fs-phone-number-field name="phoneNumber">
 *     Phone number
 *   </fs-phone-number-field>
 * </form>
 * ```
 * @element fs-phone-number-field
 * @fires input fired when the field's value changes.
 * @fires change fired when the field's value changes and is committed (typically on blur)
 * @slot - label text for the field
 * @slot leading-icon - The text field's leading icon which is typically non-interactive.
 */
export class PhoneNumberField extends FsTextField {
    /**
     * The value of the field.
     * Note: this will always return the "masked" value, the value visible in the input control. If the unmasked value is
     * needed, use the `unmaskedValue` property.
     */
    get value() {
        return this.maskedValue;
    }
    set value(value) {
        if (this.input) {
            this.reconcileValue(value);
            if (this._phoneNumberFieldManager != null && this._phoneNumberFieldManager.isConnected) {
                this._internals.setFormValue(this.useMaskedValue ? this.maskedValue : this.unmaskedValue);
            }
            else {
                this._internals.setFormValue(value);
            }
            this.reconcileValidity();
        }
        else {
            this._memoizedInitialValue = value;
        }
    }
    /**
     * The "masked" value of the input.
     * Example: 123-456-7890
     */
    get maskedValue() {
        return this._phoneNumberFieldManager?.maskedValue;
    }
    /**
     * The "unmasked" value of the input.
     * Example: 1234567890
     */
    get unmaskedValue() {
        return this._phoneNumberFieldManager?.unmaskedValue;
    }
    /**
     * The config leveraged by the PhoneNumberFieldManager for formatting options.
     *
     * @private
     */
    get _config() {
        return {
            allowInternationalNumber: this.allowInternationalNumber,
            formatOnBlur: true,
            useMaskedValue: this.useMaskedValue
        };
    }
    constructor() {
        super();
        /**
         * Whether to allow international numbers to be entered.
         **/
        this.allowInternationalNumber = fsPhoneNumberFieldDefaults.allowInternationalNumber;
        /**
         * Whether to use the masked value for the form data.
         **/
        this.useMaskedValue = fsPhoneNumberFieldDefaults.useMaskedValue;
        /**
         * Called when the PhoneNumberFieldManager reports a change to the managed input.
         * This updates the form data of any form this field is participating in.
         */
        this._handlePhoneNumberFieldChange = () => {
            this._internals.setFormValue(this.useMaskedValue ? this.maskedValue : this.unmaskedValue);
            // There is an unexpected, unexplainable bug with this specific integration with the form control mixin that causes
            // `forceError` to be set to `true`.
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            this.forceError = false;
            this.reconcileValidity();
        };
    }
    /**
     * Invoked when the component is added to the document's DOM.
     * This connects (or reconnects) the PhoneNumberFieldManager to the input and adds needed event listeners.
     */
    connectedCallback() {
        super.connectedCallback();
        if (this._phoneNumberFieldManager != null && !this._phoneNumberFieldManager.isConnected) {
            this._phoneNumberFieldManager.connect();
            this._phoneNumberFieldManager.addEventListener(phoneNumberFieldEventType['phone-number-field-input'], this._handlePhoneNumberFieldChange);
        }
    }
    /**
     * Invoked when the component is removed from the document's DOM.
     * This disconnects the PhoneNumberFieldManager from the input and removes event listeners.
     */
    disconnectedCallback() {
        super.disconnectedCallback();
        if (this._phoneNumberFieldManager?.isConnected) {
            this._phoneNumberFieldManager.disconnect();
        }
        this._phoneNumberFieldManager.removeEventListener(phoneNumberFieldEventType['phone-number-field-input'], this._handlePhoneNumberFieldChange);
    }
    /**
     * Invoked when the element is first updated.
     * Constructs and connects the PhoneNumberFieldManager to the input and adds needed event listeners.
     *
     * @param changed map of changed properties with old values
     */
    firstUpdated(changed) {
        super.firstUpdated(changed);
        if (this._memoizedInitialValue) {
            this.value = this._memoizedInitialValue;
        }
        this._phoneNumberFieldManager = new PhoneNumberFieldManager(this._fieldElement, this._config);
        this._phoneNumberFieldManager.connect();
        this._phoneNumberFieldManager.addEventListener(phoneNumberFieldEventType['phone-number-field-input'], this._handlePhoneNumberFieldChange);
    }
    render() {
        return html `
      <div class="fs-form-field ${this.compact ? 'fs-form-field--compact' : ''}">
        <label
          class="
            fs-field-label
            ${this.compact ? 'fs-field-label--compact' : ''}
            ${this.disabled ? 'fs-field-label--disabled' : ''}
            ${this.hideLabel ? 'fs-field-label--hidden' : ''}
          "
          for="input"
        >
          <slot></slot> ${this.required ? '' : html `<span class="fs-field-label__optional">(optional)</span>`}
        </label>
      <div
        class="
          fs-text-field
          ${this.compact ? 'fs-text-field--compact' : ''}
          ${this.disabled ? 'fs-text-field--disabled' : ''}
          ${this.leadingIconInteractive ? 'fs-text-field--interactive-leading' : ''}
          ${this.showError ? 'fs-text-field--error' : ''}
          ${this.readOnly ? 'fs-text-field--readonly' : ''}
        "
        @interactive-toggle="${this._interactiveIconUpdate}"
      >
        <slot name="leading-icon"></slot>
        <input
          id="input"
          autocomplete="${this.autocomplete}"
          aria-describedby="helper-text"
					?autofocus="${this.autofocus}"
					?disabled="${this.disabled}"
          inputmode="${this.inputmode}"
					max="${ifDefined(this.max)}"
					maxlength="${this.maxLength}"
					min="${ifDefined(this.min)}"
					minlength="${this.minLength}"
          name="${this.name}"
          pattern="${ifDefined(this.pattern)}"
					placeholder="${this.placeholder}"
					?readonly="${this.readOnly}"
					?required="${this.required}"
          type="${this.type}"
					@change="${this._onInputUpdate}"
          @input="${this._onInputUpdate}"
          part="fs-input__input"
        >
      </div>
      ${this.noValidate
            ? nothing
            : html `
              <span
                class="fs-helper-text ${this.showError ? 'fs-helper-text--error' : ''}"
                id="helper-text"
                aria-atomic="true"
              >
                ${!this.showError ? this.helperText : ''}
                <span aria-atomic="true" role="alert">${this.showError ? this.validationMessage : ''}</span>
              </span>`}
    </div>`;
    }
    /**
     * Called whenever the component’s update finishes and the element's DOM has been updated and rendered.
     * This handles updating the PhoneNumberFieldManager with any configuration options.
     *
     * @param changed map of changed properties with old values
     */
    updated(changed) {
        super.updated(changed);
        /** When any configuration property changes, update the currency manager's configuration */
        if (changed.has('allowInternationalNumber')) {
            this._phoneNumberFieldManager.updateConfig(this._config);
        }
        /** This adjusts how the component participates in any parent form elements */
        if (changed.has('useMaskedValue')) {
            this._phoneNumberFieldManager.updateConfig(this._config);
            this._internals.setFormValue(this.useMaskedValue ? this.maskedValue : this.unmaskedValue);
        }
    }
}
/** The element's canonical name for use with safe define */
PhoneNumberField.canonicalName = 'phone-number-field';
__decorate([
    property({ type: Boolean, attribute: 'allow-international-number' })
], PhoneNumberField.prototype, "allowInternationalNumber", void 0);
__decorate([
    property({ type: Boolean, attribute: 'use-masked-value' })
], PhoneNumberField.prototype, "useMaskedValue", void 0);
__decorate([
    property({ type: String, reflect: true })
], PhoneNumberField.prototype, "value", null);
__decorate([
    query('.fs-form-field')
], PhoneNumberField.prototype, "_fieldElement", void 0);
//# sourceMappingURL=PhoneNumberField.js.map